<template>
  <!-- The password-reset email opens `/identity/reset-password/<token>`, and
       Better Auth sends the person here with `?token=…` (or `?error=…` when
       the link is used or expired). -->
  <PreAuthPage>
    <div class="sh-card__head">
      <LogoTile :branding="branding" :alt="tenantName" :size="44" />
      <h1 class="sh-card__title">{{ t("auth.identity.reset.title") }}</h1>
    </div>

    <template v-if="done">
      <p class="reset__notice" role="status" data-test="reset-done">{{ t("auth.identity.reset.done") }}</p>
      <v-btn color="primary" size="large" block @click="toSignIn">{{ t("auth.signIn") }}</v-btn>
    </template>

    <template v-else-if="!token">
      <p class="sh-card__error" role="alert" data-test="reset-invalid">
        <v-icon size="15">error_outline</v-icon>
        <span>{{ t("auth.identity.errors.linkExpired") }}</span>
      </p>
      <v-btn color="primary" size="large" block @click="toSignIn">{{ t("auth.identity.back") }}</v-btn>
    </template>

    <form v-else class="reset__form" data-test="reset-form" @submit.prevent="submit">
      <v-text-field
        v-model="password"
        :label="t('auth.identity.reset.newPassword')"
        type="password"
        variant="outlined"
        density="comfortable"
        autocomplete="new-password"
        autofocus
        :disabled="busy"
        hide-details
      />
      <v-text-field
        v-model="confirm"
        :label="t('auth.identity.reset.confirmPassword')"
        type="password"
        variant="outlined"
        density="comfortable"
        autocomplete="new-password"
        :disabled="busy"
        hide-details
      />
      <p v-if="error" class="sh-card__error" role="alert">
        <v-icon size="15">error_outline</v-icon>
        <span>{{ error }}</span>
      </p>
      <v-btn type="submit" color="primary" size="large" block :loading="busy">{{ t("auth.identity.reset.save") }}</v-btn>
    </form>
  </PreAuthPage>
</template>

<script lang="ts">
import { computed, defineComponent, ref } from "vue";
import { useI18n } from "vue-i18n";
import { authClient, toIdentityError } from "@/auth/identity";
import { humaniseIdentityError } from "@/auth/authErrors";
import { useSessionStore } from "@/stores/session";
import LogoTile from "@/shell/LogoTile.vue";
import PreAuthPage from "@/shell/PreAuthPage.vue";

/** Mirrors the backend `minPasswordLength` / `maxPasswordLength`. */
const MIN_PASSWORD = 10;
const MAX_PASSWORD = 128;

export default defineComponent({
  name: "ResetPassword",
  components: { LogoTile, PreAuthPage },
  setup() {
    const { t } = useI18n();
    const session = useSessionStore();
    const tenantName = computed(() => session.tenant?.name || "Metaship");
    const branding = computed(() => session.tenant?.branding ?? null);

    // Read once, then take the token off the URL so it does not stay in history.
    const params = new URLSearchParams(window.location.search);
    const token = params.get("error") ? null : params.get("token");
    window.history.replaceState(null, "", window.location.pathname);

    const password = ref("");
    const confirm = ref("");
    const busy = ref(false);
    const error = ref<string | null>(null);
    const done = ref(false);

    async function submit(): Promise<void> {
      error.value = null;
      if (password.value.length < MIN_PASSWORD || password.value.length > MAX_PASSWORD) {
        error.value = t("auth.identity.errors.passwordLength");
        return;
      }
      if (password.value !== confirm.value) {
        error.value = t("auth.identity.reset.mismatch");
        return;
      }
      busy.value = true;
      try {
        const result = await authClient.resetPassword({ newPassword: password.value, token: token ?? "" });
        if (result.error) {
          error.value = humaniseIdentityError(toIdentityError(result.error));
          return;
        }
        done.value = true;
      } catch {
        error.value = t("auth.identity.reset.failed");
      } finally {
        busy.value = false;
      }
    }

    /** Leave for the sign-in page. App.vue re-reads the path on `popstate`. */
    function toSignIn(): void {
      window.history.replaceState(null, "", "/");
      window.dispatchEvent(new PopStateEvent("popstate"));
      session.returnToLogin();
    }

    return { t, tenantName, branding, token, password, confirm, busy, error, done, submit, toSignIn };
  },
});
</script>

<style scoped>
.reset__form {
  display: flex;
  flex-direction: column;
  gap: 10px;
}
.reset__notice {
  margin: 0 0 14px;
  font-size: 13px;
  color: var(--sh-text-60);
}
</style>
